import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  CircularProgress,
  Collapse,
  TextField,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import ThreeDIcon from 'assets/ThreeDIcon';
import BadgeCustom from 'components/BadgeCustom';
import UseAuthContext from 'context/AuthContext';
import UseLanguageContext from 'context/LanguageContext';
import DOMPurify from 'dompurify';
import moment from 'moment';
import { enqueueSnackbar, useSnackbar } from 'notistack';
import IdfParseResultStep from 'components/IdfParseResultStep';
import RevisionError from 'pages/Revisions/RevisionError';
import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { getAllRevisionFiles } from 'services/project-service';
import { generateViewerUrl, runRevision, getFeatures, getRevisionResult, getRevisionById } from 'services/revisions';
import { downloadValidationReport, generateShareableReportLink } from 'services/inspections-service';
import { markRevisionPendingOptimistic, clearRevisionPendingOptimistic } from 'hooks/useRevision';
import { USER_ROLES } from 'utils/constants';
import { RevisionStatus } from 'utils/contracts';
import { formatFileName } from 'utils/Formatter';
import { isCvViewerEnabled } from 'utils/cvViewer';
import { isProjectVersionV3OrV4, isShgcViewerEnabled } from 'utils/shgcViewer';
import { extractNameFixes, extractParseErrors, isZoneNameErrorCode, ZoneNameFix } from 'utils/idfParse';
import { getErrorHtmlContent, getLatestExpiryDate, orgCanRunSimulation } from 'utils/utils';
import { fireSuccessConfetti } from 'utils/successConfetti';
import { TypographyLocale } from 'components/TypographyLocale';
import ButtonCustom from './ButtonCustom';
import ButtonIcon from './ButtonIcon';
import ButtonIconSvg from './ButtonIconSvg';
import NewFeatureBadge from './NewFeatureBadge';
import CustomTableCell from './CustomTableCell';
import GradeBadge from './GradeBadge';
import Restrict from './Restrict';
import GenericDialog from './GenericDialog';

export const RevisionNotes: React.FC<{ notes: string }> = ({ notes }) => {
  const formattedNotes = notes.replace(/\n/g, '<br/>');

  return (
    <div
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(formattedNotes, {
          ADD_ATTR: ['target'],
          ADD_TAGS: ['br']
        })
      }}
    />
  );
};

export const InspectionStatusBadge: React.FC<{ inspection?: any, rtl?: boolean }> = ({ inspection, rtl }) => {
  const { t } = useTranslation();
  
  // Check if there's a laboratory assignment (targetOrganization present = laboratory assignment)
  const hasLaboratoryAssignment = inspection?.targetOrganization != null;
  
  // Handle backward compatibility with old status field
  const userStatus = inspection?.userStatus || (inspection?.hasUserChecks ? 'in_progress' : 'not_started');
  const inspectorStatus = inspection?.inspectorStatus || inspection?.status || null;
  const hasUserChecks = inspection?.hasUserChecks || false;
  const hasInspectorAssignment = inspection?.hasInspectorAssignment || (inspection?.status && inspectorStatus !== null);

  // If no inspection data at all, return empty
  if (!inspection || (!hasUserChecks && !hasInspectorAssignment)) {
    return <span style={{ color: '#666', fontSize: '14px' }}></span>;
  }
  
  // If there's a laboratory assignment, only show inspector status (hide user status)
  if (hasLaboratoryAssignment) {
    if (!hasInspectorAssignment) {
      return <span style={{ color: '#666', fontSize: '14px' }}></span>;
    }
  }



  const getInspectorStatusLabel = (status: string) => {
    switch (status) {
      case 'awaiting':
        return t('REVISION_INSPECTION_STATUS_AWAITING');
      case 'reviewed':
        return t('REVISION_INSPECTION_STATUS_REVIEWED');
      case 'checked':
        return t('REVISION_INSPECTION_STATUS_CHECKED');
      case 'pending':
        return t('REVISION_INSPECTION_STATUS_PENDING');
      default:
        return status || '';
    }
  };

  const getInspectorStatusColor = (status: string) => {
    switch (status) {
      case 'awaiting':
        return '#ACACAC'; // Grey
      case 'reviewed':
        return '#1899FF'; // Blue
      case 'checked':
        return '#1bb81e'; // Green
      case 'pending':
        return '#ff9800'; // Orange
      default:
        return '#f5f5f5';
    }
  };

  // Build combined status display
  let statusText = '';
  let backgroundColor = '#f5f5f5';
  let textColor = '#666';

  // If there's a laboratory assignment, only show inspector status (hide user status)
  if (hasLaboratoryAssignment) {
    // Only show inspector status when there's a laboratory assignment
    statusText = `${getInspectorStatusLabel(inspectorStatus)}`;
    backgroundColor = getInspectorStatusColor(inspectorStatus);
    textColor = '#ffffff';
  } /* else if (hasUserChecks && hasInspectorAssignment) {
    // Both exist - show combined (only when no laboratory assignment)
    statusText = `User: ${getUserStatusLabel(userStatus)} | Inspector: ${getInspectorStatusLabel(inspectorStatus)}`;
    // Use inspector status color as primary, or user if inspector is null
    backgroundColor = inspectorStatus ? getInspectorStatusColor(inspectorStatus) : getUserStatusColor(userStatus);
    textColor = '#ffffff';
  } else if (hasUserChecks) {
    // Only user checks
    statusText = `${getUserStatusLabel(userStatus)}`;
    backgroundColor = getUserStatusColor(userStatus);
    textColor = '#ffffff';} */
   else if (hasInspectorAssignment) {
    // Only inspector assignment
    statusText = `${getInspectorStatusLabel(inspectorStatus)}`;
    backgroundColor = getInspectorStatusColor(inspectorStatus);
    textColor = '#ffffff';
  }

  return (
    <span 
      style={{ 
        backgroundColor,
        color: textColor,
        fontSize: '14px',
        fontWeight: '600',
        height: '36px',
        width: rtl ? '100px' : '100px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 8px',
        borderRadius: '3px',
        textAlign: 'center',
        lineHeight: '1.2',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
      title={statusText}
    >
      {statusText}
    </span>
  );
};

export const downloadFile = async (url: string, filename: string, auth: any, t: any) => {
  let name = filename;
  try {
    if (filename.includes('output')) {
      const urlParts = url.split('/');
      const fileId = urlParts[urlParts.length - 3]
      name = `output_${fileId}.json`;
    }
    if (filename.includes('input')) {
      const urlParts = url.split('/');
      const fileId = urlParts[urlParts.length - 3]
      name = `input_${fileId}.json`;
    }
    if (filename.includes('inspection')) {
      const urlParts = url.split('/');
      const fileId = urlParts[urlParts.length - 3]
      name = `inspection_${fileId}.zip`;
    }
    if (filename.includes('idf')) {
      const urlParts = url.split('/');
      const fileId = urlParts[urlParts.length - 3]
      name = `${fileId}.idf`;
    }
    const response = await fetch(`${url}?authorization=${auth?.token}`);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    enqueueSnackbar(t('ERROR_DOWNLOADING_FILE'), { variant: 'error' });
  }
};

// Function to copy text to clipboard
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    }
  } catch (error) {
    return false;
  }
};

const RevisionRowData = ({
  props,
  projectId,
  index,
  refetchData,
  handleClickOpen,
  OrgId,
  hasTrial,
  totalPoints,
  projectType,
  projectVersion,
  handleClickCreditPopUpOpen,
  handleClickDeletePopUpOpen,
  has3DStartDateAllowed,
  hasProjectThreeDViewerAllowed,
  isInspectorAllowedStartDate,
  isInspectorAllowed,
  showInspectionStatusColumn,
  hasInspectorAssignment
}: any) => {
  const {
    id,
    createdBy,
    updatedAt,
    startedAt,
    status,
    transaction_id,
    requiredPoints,
    ticket,
    notes,
    weighed_grade,
    rating,
    files,
    h_report_pass,
    report_1045_pass,
    skip_overwrite,
  } = props;
  const { t } = useTranslation();
  const { language } = useContext(UseLanguageContext);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const {
    role: authorization,
    organizations: organizationDetails,
    auth,
    organizationId,
  } = useContext(UseAuthContext);
  const { data: features } = useQuery('features', getFeatures);
  const hasUnlimited = organizationDetails?.hasUnlimited;
  const latestExpiryDate = organizationDetails ? getLatestExpiryDate(organizationDetails) : null;
  // Check if revision was started before or at the latest expiry/cancellation date
  const isRevisionStartedBeforeExpiry = latestExpiryDate && startedAt 
    ? new Date(startedAt) <= latestExpiryDate 
    : false;
  const [showRevisionError, setShowRevisionError] = useState(false);
  const [showLabsParseDialog, setShowLabsParseDialog] = useState(false);
  const [labsParseErrors, setLabsParseErrors] = useState<string[]>([]);
  const [labsNameFixes, setLabsNameFixes] = useState<ZoneNameFix[]>([]);
  const [labsZoneCount, setLabsZoneCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openRow, setOpenRow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);
  const [hover, setHover] = useState(false);
  const [showShareLinkDialog, setShowShareLinkDialog] = useState(false);
  const [shareLinkData, setShareLinkData] = useState<{ url: string } | null>(null);
  const [isGeneratingShareLink, setIsGeneratingShareLink] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [openMembershipDialog, setOpenMembershipDialog] = useState(false);
  const isViewer = authorization === USER_ROLES.VIEWER;
  const isRevisionSuccess = status === RevisionStatus.SUCCESS;
  const hasTransactionId = Boolean(transaction_id);
  const isViewerWithRevisionSuccessAndTransactionId = (isViewer && isRevisionSuccess && hasTransactionId)
  const canDownloadRevision = !!transaction_id || (hasUnlimited && isRevisionSuccess) || isRevisionStartedBeforeExpiry;
  const has3DRevisionStartDateAllowed = new Date(updatedAt) > new Date(has3DStartDateAllowed);
  const showShgcViewer =
    status === RevisionStatus.SUCCESS &&
    isShgcViewerEnabled(features, projectVersion, updatedAt);
  const showCvViewer =
    status === RevisionStatus.SUCCESS &&
    isCvViewerEnabled(features, projectVersion, updatedAt);

  const handleOpenShgcViewer = (e: React.MouseEvent) => {
    e.stopPropagation();
    const code = btoa(String(id));
    const viewerUrl = projectId
      ? `/shgc-viewer?code=${code}&revisionId=${id}&projectId=${projectId}`
      : `/shgc-viewer?code=${code}&revisionId=${id}`;
    window.open(viewerUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenCvViewer = (e: React.MouseEvent) => {
    e.stopPropagation();
    const code = btoa(String(id));
    const viewerUrl = projectId
      ? `/cv-viewer?code=${code}&revisionId=${id}&projectId=${projectId}`
      : `/cv-viewer?code=${code}&revisionId=${id}`;
    window.open(viewerUrl, '_blank', 'noopener,noreferrer');
  };

  // v3/v4 only — PIXO / report share links must not appear on v2.
  const isV3orV4 = isProjectVersionV3OrV4(projectVersion);

  const isInspectorRole =
    authorization === USER_ROLES.INSPECTOR ||
    authorization === USER_ROLES.INSPECTOR_MANAGER;
  // IDF converted (diagnosis available) but simulation not started yet.
  const canRunSimulationFromMenu =
    (authorization === USER_ROLES.ADMIN || isInspectorRole) &&
    Boolean(files?.idf_epjson?.url) &&
    [
      RevisionStatus.CHECKED,
      RevisionStatus.FAILED,
      RevisionStatus.INIT,
    ].includes(status);
  // Checker page: before sim, after SUCCESS, and while a ticket is RESOLVING.
  // Soft parser warnings keep status `checked`; SUCCESS must still open /check for inspectors.
  const canOpenInspectorPage =
    isV3orV4 &&
    Boolean(props.files?.idf_epjson) &&
    (authorization === USER_ROLES.ADMIN || isInspectorRole) &&
    [
      RevisionStatus.CHECKED,
      RevisionStatus.FAILED,
      RevisionStatus.INIT,
      RevisionStatus.SUCCESS,
      RevisionStatus.RESOLVING,
    ].includes(status);

  const openInspectorPage = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    handleClose();
    window.open(
      `${window.location.origin}/report/${id}/check`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const hasOpenOrOngoingTicket = Boolean(
    props.ticket &&
      ['open', 'ongoing'].includes(String(props.ticket.status || '').toLowerCase())
  );

  const canShowDeleteRevision =
    ([
      RevisionStatus.VALIDATING,
      RevisionStatus.FAILED,
      RevisionStatus.CHECKED,
      RevisionStatus.SUCCESS,
      RevisionStatus.INIT,
    ].includes(status) ||
      hasOpenOrOngoingTicket) &&
    !canDownloadRevision;

  const menuBasedOnStatus = () => {
    let items = 0;
    let editLink, downloadReportLinks, deleteLink, chartLink;
    const canShowEdit =
      authorization !== USER_ROLES.VIEWER &&
      ([
        RevisionStatus.SUCCESS,
        RevisionStatus.CHECKED,
        RevisionStatus.INIT,
      ].includes(status) ||
        (isInspectorRole && status === RevisionStatus.FAILED));

    if (canShowEdit) {
      if (status === RevisionStatus.SUCCESS) {
        editLink = (
          <>
            <MenuItem
              key="edit"
              onClick={(e) => {
                handleClose();
                EditRevision(e, id);
              }}
            >
              {t('REVISION_MENU_EDIT')}
            </MenuItem>
            <MenuItem
              key="view"
              onClick={(e) => {
                handleClose();
                ViewResult(e, id);
              }}
            >
              {t('REVISION_MENU_VIEW_RESULT')}
            </MenuItem>
          </>
        );
      } else {
        editLink = (
          <MenuItem
            key="edit"
            onClick={(e) => {
              handleClose();
              EditRevision(e, id);
            }}
          >
            {t('REVISION_MENU_EDIT')}
          </MenuItem>
        );
      }
      items++;
    }
    if (canDownloadRevision) {
      items++;
    }

    downloadReportLinks = (
      <>
        {canDownloadRevision && props.files?.full && (
          <a
            key="full"
            href={`${props.files?.full?.url}?authorization=${auth?.token}`}
            rel='noopener noreferrer'
            download
            className='dropdown-menu__link'
          >
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_DETAILED_REPORT')}
            </MenuItem>
          </a>
        )}
        {canDownloadRevision && props.files?.rating && (
          <a
            key="rating"
            href={`${props.files?.rating?.url}?authorization=${auth?.token}`}
            rel='noopener noreferrer'
            download
            className='dropdown-menu__link'
          >
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_RATING_REPORT')}
            </MenuItem>
          </a>
        )}

        {canDownloadRevision && props.files?.dxf && (
          <a
            key="dxf"
            href={`${props.files?.dxf?.url}?authorization=${auth?.token}`}
            rel='noopener noreferrer'
            download
            className='dropdown-menu__link'
          >
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_3D_REPORT')}
            </MenuItem>
          </a>
        )}
        {canDownloadRevision && props.files?.h_report && (
          <a
            key="h_report"
            href={`${props.files?.h_report?.url}?authorization=${auth?.token}`}
            rel='noopener noreferrer'
            download
            className='dropdown-menu__link'
          >
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_H_REPORT')}{' '}
            </MenuItem>
          </a>
        )}

        {canDownloadRevision && props.files?.report_1045 && (
          <a
            key="report_1045"
            href={`${props.files?.report_1045?.url}?authorization=${auth?.token}`}
            rel='noopener noreferrer'
            download
            className='dropdown-menu__link'
          >
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_1045_REPORT')}
            </MenuItem>
          </a>
        )}
        {authorization === USER_ROLES.ADMIN && props.files?.idf && (
          <a
            key="idf"
            onClick={() => downloadFile(
              props.files?.idf?.url,
              props.files?.idf?.name || 'model.idf',
              auth,
              t
            )}
          >
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_IDF_FILE')}
            </MenuItem>
          </a>
        )}
        {authorization === USER_ROLES.ADMIN && props.files?.mod_idf && (
          <a
            key="mod_idf"
            href={`${props.files?.mod_idf?.url}?authorization=${auth?.token}`}
            rel='noopener noreferrer'
            download
            className='dropdown-menu__link'
          >
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_RUN_FILE')}
            </MenuItem>
          </a>
        )}
        {authorization === USER_ROLES.ADMIN && props.files?.output_json && (
          <MenuItem
            key="output_json"
            onClick={() => downloadFile(
              props.files?.output_json?.url,
              props.files?.output_json?.name || 'output.json',
              auth,
              t
            )}>
            {t('REVISION_MENU_DOWNLOAD_OUTPUT_JSON_FILE')}
          </MenuItem>
        )}
        {authorization === USER_ROLES.ADMIN && props.files?.input_json && (
          <MenuItem
            key="input_json"
            onClick={() => downloadFile(
              props.files?.input_json?.url,
              props.files?.input_json?.name || 'input.json',
              auth,
              t
            )}>
            {t('REVISION_MENU_DOWNLOAD_INPUT_JSON_FILE')}
          </MenuItem>
        )}
        {/*      {authorization === USER_ROLES.ADMIN &&
          status !== RevisionStatus.FAILED &&
          props.files?.dsb && (
            <MenuItem>
              <a
                href={`${props.files.dsb.url}?authorization=${auth?.token}`}
                rel='noopener noreferrer'
                download
                className='dropdown-menu__link'
              >
                {t('REVISION_MENU_DOWNLOAD_DSB_MODEL_FILE')}
              </a>
            </MenuItem>
          )}
 */}
        {authorization === USER_ROLES.ADMIN &&
          status === RevisionStatus.SUCCESS &&
          props.files?.h_report_csv && (
            <a
              key="h_report_csv"
              href={`${props.files?.h_report_csv?.url}?authorization=${auth?.token}`}
              rel='noopener noreferrer'
              download
              className='dropdown-menu__link'
            >
              <MenuItem>
                {t('REVISION_MENU_DOWNLOAD_CSV_FILE')}
              </MenuItem>
            </a>
          )}
        {authorization === USER_ROLES.ADMIN && props?.files?.idf_epjson?.url && (
          <MenuItem
            key="idf_epjson"
            onClick={() => {
              downloadFile(
                props.files?.idf_epjson?.url,
                props.files?.idf_epjson?.name || 'inspection.zip',
                auth,
                t
              );
            }}
          >
            {t('REVISION_MENU_DOWNLOAD_EPJSON_FILE')}
          </MenuItem>
        )}
        {/* Download PDF Report - Admin only for now (future: users too) */}
        {status === RevisionStatus.SUCCESS && authorization === USER_ROLES.ADMIN && (
          <MenuItem
            key="download-pdf-report"
            onClick={async (e) => {
              e.stopPropagation();
              try {
                await downloadValidationReport(id);
              } catch (error: any) {
                enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
              }
            }}
          >
            {t('REVISION_MENU_DOWNLOAD_PDF_REPORT') || 'Download PDF Report'}
          </MenuItem>
        )}
        {/* SHGCmax/Um — before Inspection link */}
        {showShgcViewer &&
          (canDownloadRevision || authorization === USER_ROLES.ADMIN) && (
          <MenuItem key="shgc-viewer" onClick={handleOpenShgcViewer}>
            {t('SHGC_VIEWER_BUTTON')}
          </MenuItem>
        )}
        {showCvViewer &&
          (canDownloadRevision ||
            authorization === USER_ROLES.ADMIN ||
            isInspectorRole) && (
          <MenuItem key="cv-viewer" onClick={handleOpenCvViewer}>
            {t('CV_VIEWER_BUTTON')}
          </MenuItem>
        )}
        {/* Admin + inspector: one inspector link to /report/:id/check */}
        {canOpenInspectorPage && (
          <MenuItem key="open-inspector-link" onClick={openInspectorPage}>
            {t('REVISION_MENU_OPEN_REPORT_LINK')}
          </MenuItem>
        )}
        {/* Copy report link — after simulation (non-inspector). Skip when the inspector link above already covers this. */}
        {isV3orV4 &&
         status === RevisionStatus.SUCCESS && 
         !isInspectorRole &&
         !canOpenInspectorPage &&
         (authorization === USER_ROLES.ADMIN || canDownloadRevision) &&
         (props.files?.idf_epjson || props.files?.full || props.files?.rating) &&
         (authorization === USER_ROLES.ADMIN || (features?.popupChecklistAllowed && features?.popupChecklistAllowedStartDate && startedAt && new Date(startedAt) >= new Date(features?.popupChecklistAllowedStartDate))) && (
          <MenuItem
            key="copy-report-link"
            onClick={async (e) => {
              e.stopPropagation();
              // Admin: open /check so InspectionGate runs. Users: copy plain report link.
              const reportLink = authorization === USER_ROLES.ADMIN
                ? `${window.location.origin}/report/${id}/check`
                : `${window.location.origin}/report/${id}`;
              
              if (authorization === USER_ROLES.ADMIN) {
                window.open(reportLink, '_blank', 'noopener,noreferrer');
              } else {
                try {
                  await navigator.clipboard.writeText(reportLink);
                  enqueueSnackbar(t('REVISION_MENU_COPY_REPORT_LINK_SUCCESS') || 'Report link copied to clipboard', { variant: 'success' });
                } catch (error: any) {
                  enqueueSnackbar(t('REVISION_MENU_COPY_REPORT_LINK_ERROR') || 'Failed to copy report link', { variant: 'error' });
                }
              }
            }}
          >
            {authorization === USER_ROLES.ADMIN
              ? (t('REVISION_MENU_OPEN_REPORT_LINK') || 'Open Report Link')
              : (t('REVISION_MENU_COPY_REPORT_LINK') || 'Copy Report Link')}
          </MenuItem>
        )}
        {/* Generate Shareable Link Pixo - v3/v4 (all roles including Admin), gated by org flag */}
        {isV3orV4 &&
         status === RevisionStatus.SUCCESS && 
         (authorization === USER_ROLES.ADMIN || canDownloadRevision) &&
         authorization !== USER_ROLES.INSPECTOR &&
         authorization !== USER_ROLES.INSPECTOR_MANAGER &&
         (authorization === USER_ROLES.ADMIN || (features?.pixoLinkAllowed && (!features?.pixoLinkAllowedStartDate || (startedAt && new Date(startedAt) >= new Date(features.pixoLinkAllowedStartDate))))) && (
          <MenuItem
            key="generate-shareable-link"
            onClick={async (e) => {
              e.stopPropagation();
              await handleGenerateShareableLink();
            }}
            disabled={isGeneratingShareLink}
          >
            {isGeneratingShareLink ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('REVISION_MENU_GENERATING_SHARE_LINK') || 'Generating Share Link...'}
              </>
            ) : (
              t('REVISION_MENU_GENERATE_SHAREABLE_LINK') || 'Generate Shareable Link'
            )}
          </MenuItem>
        )}
        {/* Assign Inspector - v3 only (all roles including Admin) */}
        {/* {isV3orV4 &&
         (((authorization === USER_ROLES.ADMIN) && status === RevisionStatus.SUCCESS) ||
          (authorization === USER_ROLES.MANAGER && isInspectorAllowed && isInspectorAllowedStartDate && isRevisionSuccess && hasTransactionId)) &&
          !hasLaboratoryAssignment && props?.files?.idf_epjson && (
          <MenuItem
            onClick={() => {
              (window as any).openShareInspector?.(id)
            }}
          >
            {t('REVISION_MENU_ASSIGN_INSPECTOR')}
          </MenuItem>
        )} */}

        {/* URL Generation - Admin Only */}
        {authorization === USER_ROLES.ADMIN && status === RevisionStatus.SUCCESS && props?.files?.idf_epjson && (
          <MenuItem
            onClick={handleGenerateUrl}
            disabled={isGeneratingUrl}
          >
            {isGeneratingUrl ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('REVISION_MENU_GENERATING_URL')}
              </>
            ) : (
              t('REVISION_MENU_GENERATE_URL')
            )}
          </MenuItem>
        )}

        {/* Same moment as diagnosis (IDF converted, sim not started) — run sim from the ⋮ menu */}
        {canRunSimulationFromMenu && (
          <MenuItem
            key="run-simulation"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
              requestRunSimulation();
            }}
          >
            {t('REVISION_MENU_RUN_SIMULATION')}
          </MenuItem>
        )}

      </>
    );

    if (canShowDeleteRevision) {
      items++;
      deleteLink = (
        <MenuItem
          key="delete"
          onClick={(e) => handleClickDeletePopUpOpen(id)}>
          {t('REVISION_MENU_DELETE_REVISION')}
        </MenuItem>
      );
    }

    return [
      <Fragment key="menu-items">
        {editLink}
        {downloadReportLinks}
        <Restrict forbidden={['viewer']}>{deleteLink}</Restrict>
      </Fragment>,
      items > 0,
    ];
  };


  const requestRunSimulation = () => {
    if (isInspectorRole && !orgCanRunSimulation(organizationDetails)) {
      setOpenMembershipDialog(true);
      return;
    }
    enqueueSnackbar(t('IDF_LAB_SIM_LAUNCHED'), { variant: 'success' });
    void run();
  };

  const run = async () => {
    if (isInspectorRole && !orgCanRunSimulation(organizationDetails)) {
      setOpenMembershipDialog(true);
      return;
    }
    markRevisionPendingOptimistic(queryClient, projectId, id);
    try {
      const data = await runRevision(projectId, id, OrgId);
      if (data.status === 200) {
        refetchData();
      }
    } catch (error: any) {
      clearRevisionPendingOptimistic(id);
      queryClient.invalidateQueries(['GetAllRevisions', projectId]);
      if (error) {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
      }
    }
  };

  const handleDownload = async () => {
    setIsLoading(true);
    // Subscribers never see Generate Report (download is already active), so celebrate on this click.
    if (!transaction_id && (hasUnlimited || isRevisionStartedBeforeExpiry)) {
      fireSuccessConfetti();
    }
    try {
      await getAllRevisionFiles(OrgId, projectId, id);
    } catch (error) {
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const showError = async () => {
    const useLabsParsePopup =
      authorization === USER_ROLES.ADMIN || isInspectorRole;
    if (!useLabsParsePopup) {
      setShowRevisionError(true);
      return;
    }

    const isGenericParseFallback = (list: string[]) =>
      list.length === 0 ||
      list.every(
        (e) => e === 'REVISION_PARSE_FAILED' || e === 'IDF_LAB_PARSE_FAILED'
      );

    const fromResultPayload = (payload: any): string[] => {
      const err = payload?.error || payload?.data?.error;
      const list = err?.message;
      if (Array.isArray(list)) {
        return list.map((e: unknown) => String(e)).filter(Boolean);
      }
      return extractParseErrors(payload, { genericFallback: false });
    };

    let revisionPayload: any = props;
    let errors = extractParseErrors(props, { genericFallback: false });
    if (isGenericParseFallback(errors)) {
      try {
        const res = await getRevisionResult(id, projectId, OrgId);
        revisionPayload = res?.data ?? revisionPayload;
        errors = fromResultPayload(res?.data);
      } catch {
        errors = [];
      }
    }
    if (isGenericParseFallback(errors)) {
      try {
        const res = await getRevisionById(id, projectId, OrgId);
        revisionPayload = res?.data ?? revisionPayload;
        errors = extractParseErrors(res?.data, { genericFallback: false });
      } catch {
        errors = errors.length ? errors : ['GENERAL_ERROR'];
      }
    }

    const isEnergyPlus = errors.some(
      (m) => typeof m === 'string' && m.includes('Program')
    );
    if (isEnergyPlus) {
      setShowRevisionError(true);
      return;
    }

    const noZones = errors.some(
      (e) => e === 'REVISION_MISSING_ZONES' || e === 'IDF_LAB_NO_ZONES'
    );
    if (
      errors.some((code) => isZoneNameErrorCode(code)) &&
      extractNameFixes(revisionPayload).length === 0
    ) {
      try {
        const res = await getRevisionById(id, projectId, OrgId);
        revisionPayload = res?.data ?? revisionPayload;
      } catch {
        // Keep the error list already resolved; the name table stays empty.
      }
    }
    setLabsParseErrors(errors.length ? errors : ['GENERAL_ERROR']);
    setLabsNameFixes(extractNameFixes(revisionPayload));
    setLabsZoneCount(noZones ? 0 : 1);
    setShowLabsParseDialog(true);
  };

  const continueLabsParseFromBoard = (fixNames = false) => {
    if (labsZoneCount <= 0) return;
    setShowLabsParseDialog(false);
    ViewDiagnosis(undefined, id, { analyze: true, fixNames });
  };

  const handleGenerateUrl = async () => {
    setIsGeneratingUrl(true);
    try {
      const response = await generateViewerUrl(id);
      if (response.url) {
        // Open URL in new window
        window.open(response.url, '_blank', 'noopener,noreferrer');
        enqueueSnackbar(t('REVISION_MENU_URL_OPENED_SUCCESS'), { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
    } finally {
      setIsGeneratingUrl(false);
    }
  };

  const handleGenerateShareableLink = async () => {
    setIsGeneratingShareLink(true);
    try {
      const response = await generateShareableReportLink(id);
      setShareLinkData(response);
      setShowShareLinkDialog(true);
    } catch (error) {
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
    } finally {
      setIsGeneratingShareLink(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (shareLinkData?.url) {
      const success = await copyToClipboard(shareLinkData.url);
      if (success) {
        setIsCopied(true);
        enqueueSnackbar(t('REVISION_MENU_COPY_REPORT_LINK_SUCCESS') || 'Shareable link copied to clipboard', { variant: 'success' });
        // Reset icon after 2 seconds
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      } else {
        enqueueSnackbar(t('REVISION_MENU_COPY_REPORT_LINK_ERROR') || 'Failed to copy shareable link', { variant: 'error' });
      }
    }
  };
  const ButtonsBasedOnStatus = () => {
    let action,
      text,
      disabled = '' as any,
      styleBtn = '',
      color = null,
      icon = null,
      menuDisabled = false;
    switch (status) {
      case RevisionStatus.INIT:
        text = t('REVISION_INIT_STATUS_BUTTON_TEXT');
        disabled = 'revision-btn-validating';
        styleBtn = 'revision-btn-pending';
        if (authorization === USER_ROLES.VIEWER) {
          styleBtn += ' revision-btn-hide';
        }
        break;
      case RevisionStatus.CHECKED: {
        const hasEpjson = Boolean(files?.idf_epjson?.url);
        const canOpenDiagnosis =
          hasEpjson &&
          (authorization === USER_ROLES.ADMIN || isInspectorRole);
        if (canOpenDiagnosis) {
          // Labs convert (clean parse): stay on Diagnosis until simulation starts.
          text = t('REVISION_MENU_DIAGNOSIS');
          action = {
            onClick: (e: any) => {
              e?.stopPropagation?.();
              ViewDiagnosis(e, id);
            },
          };
          styleBtn = 'revision-btn-diagnosis';
        } else {
          action = { onClick: run };
          text = t('REVISION_CHECKED_STATUS_BUTTON_TEXT');
          styleBtn = 'revision-btn-run';
          if (authorization === USER_ROLES.VIEWER) {
            styleBtn += ' revision-btn-hide';
          }
        }
        break;
      }
      case RevisionStatus.FAILED: {
        const hasEpjson = Boolean(files?.idf_epjson?.url);
        const canOpenDiagnosis =
          hasEpjson &&
          (authorization === USER_ROLES.ADMIN || isInspectorRole);
        if (canOpenDiagnosis) {
          // After "I understand" + convert (epJSON present): Diagnosis, not Open Error.
          text = t('REVISION_MENU_DIAGNOSIS');
          action = {
            onClick: (e: any) => {
              e?.stopPropagation?.();
              ViewDiagnosis(e, id);
            },
          };
          styleBtn = 'revision-btn-diagnosis';
        } else {
          text = t('REVISION_ERROR_STATUS_BUTTON_TEXT');
          action = { onClick: showError };
          styleBtn = 'revision-btn-failed';
        }
        break;
      }
      case RevisionStatus.VALIDATING:
        text = t('REVISION_VALIDATING_STATUS_BUTTON_TEXT');
        disabled = 'revision-btn-validating';
        styleBtn = 'revision-btn-validating';
        icon = <CircularProgress thickness={2} sx={{ color: 'white', ml: language === 'he' ? '3px' : 0, mr: language === 'he' ? 0 : '3px' }} size={20} color="inherit" className='animation' />;
        break;
      case RevisionStatus.RESOLVING:
        if (!ticket) {
          text = t('REVISION_PENDING_STATUS_BUTTON_TEXT');
          disabled = 'revision-row-pending';
          styleBtn = 'revision-btn-pending spinning-grey';
          icon = <CircularProgress thickness={2} sx={{ color: 'grey', ml: language === 'he' ? '12px' : 0, mr: language === 'he' ? 0 : '12px' }} size={20} color="inherit" className='animation' />;
        } else {
          switch (ticket.status.toLowerCase()) {
            case 'open':
              action = {
                onClick: (e: any) => {
                  let url =
                    authorization === USER_ROLES.ADMIN
                      ? `/admin/business/${OrgId}/project/${projectId}/edit-ticket-dsb/${id}`
                      : `/project/${projectId}/edit-ticket-dsb/${id}`;
                  navigate(url);
                  e.stopPropagation();
                },
              };
              text = t('REVISION_RESOLVING_STATUS_CREATE_TICKET_BUTTON_TEXT');
              if (authorization === USER_ROLES.VIEWER) {
                styleBtn += ' revision-btn-hide';
              }
              //styleBtn = 'revision-btn-resolving-open';
              color = 'error';
              break;
            case 'ongoing':
              // Todo change to upload all files Page URL below code
              if (authorization === USER_ROLES.ADMIN) {
                action = {
                  onClick: (e: any) => {
                    let url =
                      authorization === USER_ROLES.ADMIN
                        ? `/admin/business/${OrgId}/project/${projectId}/revision/${id}/${ticket?.id}`
                        : `/project/${projectId}/revision/${id}`;
                    navigate(url);
                    e.stopPropagation();
                  },
                };
                text = t('REVISION_RESOLVING_STATUS_OPEN_TICKET_BUTTON_TEXT');
                //styleBtn = 'revision-btn-resolving-ongoing';
                color = 'orange';
              } else {
                text = t(
                  'REVISION_RESOLVING_STATUS_ONGOING_TICKET_BUTTON_TEXT'
                );
                disabled = 'revision-row-pending';
                styleBtn = 'revision-btn-pending spinning-grey';
                icon = <CircularProgress thickness={2} size={20} color="inherit" className='animation' />
              }
              break;
            default:
              break;
          }
        }
        break;
      case RevisionStatus.PROCESSING:
      case RevisionStatus.PENDING:
        text = t('REVISION_PENDING_STATUS_BUTTON_TEXT');
        disabled = 'revision-row-pending';
        styleBtn = 'revision-btn-pending spinning-grey';
        icon = <CircularProgress thickness={2} sx={{ color: 'grey' /* ml: language === 'he' ? '12px' : 0, mr: language === 'he' ? 0 : '12px' */ }} size={20} color="inherit" className='animation' />;
        break;
      case RevisionStatus.SUCCESS:
        if (transaction_id || hasUnlimited || isRevisionStartedBeforeExpiry) {
          text = t('REVISION_SUCCESS_DOWNLOAD_BUTTON_TEXT');
          styleBtn = 'revision-btn-success';
          action = { onClick: handleDownload } as any;
          if (isLoading) {
            action = ""
            text = t('REVISION_PENDING_STATUS_BUTTON_TEXT');
            disabled = 'revision-row-pending';
            styleBtn = 'revision-btn-pending spinning-grey';
            icon = <CircularProgress thickness={2} sx={{ color: 'grey' }} size={20} color="inherit" className='animation' />
          };
        } else if (
          hasTrial ||
          (Number.isFinite(Number(requiredPoints)) &&
            Number(requiredPoints) > 0 &&
            Number(totalPoints ?? 0) >= Number(requiredPoints))
        ) {
          action = { onClick: () => handleClickOpen({ id, requiredPoints }) };
          text = t('REVISION_SUCCESS_UNLOCK_BUTTON_TEXT')
          //styleBtn = 'revision-btn-success-locked';
          color = 'primary';
          if (['viewer'].includes(authorization ?? '')) {
            styleBtn += ' revision-btn-hide';
          }
        }
        else {
          action = { onClick: handleClickCreditPopUpOpen };
          text = t(`REVISION_SUCCESS_TOPUP_BUTTON_TEXT`);
          styleBtn = 'revision-btn-buy';
          if (['viewer'].includes(authorization ?? '')) {
            styleBtn += ' revision-btn-hide';
          }
        }
        break;
      default:
        break;
    }
    const isEnabled =
      action && action.onClick !== undefined && action.onClick !== null;
    const disabledAttribute = !isEnabled
      ? { disabled: true }
      : { disabled: false };

    return (
      <ButtonCustom
        sx={{
          mx: '-13px',
        }}
        size={language === 'he' ? 'extra-small' : 'small'}
        fullWidth
        {...color && { color: color }}
        variant='contained'
        className={styleBtn}
        {...action}
        {...disabledAttribute}
        {...(icon && {
          [language === 'he' ? 'endIcon' : 'startIcon']: icon,

        })}
      >
        {text}
      </ButtonCustom>
    );
  };

  const EditRevision = (e: any, revisionID: any) => {
    e?.stopPropagation?.();
    const orgId = OrgId || organizationId;
    const useLabs =
      authorization === USER_ROLES.ADMIN || isInspectorRole;
    if (authorization === USER_ROLES.ADMIN) {
      navigate(
        useLabs
          ? `/admin/business/${orgId}/project/${projectId}/edit-revision-labs/${revisionID}`
          : `/admin/business/${orgId}/project/${projectId}/edit-revision/${revisionID}`
      );
    } else {
      navigate(
        useLabs
          ? `/project/${projectId}/edit-revision-labs/${revisionID}`
          : `/project/${projectId}/edit-revision/${revisionID}`
      );
    }
  };

  const ViewResult = (e: any, revisionID: any) => {
    if (authorization === USER_ROLES.ADMIN) {
      navigate(
        `/admin/business/${OrgId}/project/${projectId}/revision/${revisionID}/result`
      );
    } else {
      navigate(
        `/business/${OrgId}/project/${projectId}/revision/${revisionID}/result`
      );
    }
  };

  const ViewDiagnosis = (
    e: any,
    revisionID: any,
    options?: { analyze?: boolean; fixNames?: boolean }
  ) => {
    e?.stopPropagation?.();
    const navOpts = options?.analyze
      ? { state: { analyze: true, fixNames: options.fixNames === true } }
      : undefined;
    if (authorization === USER_ROLES.ADMIN) {
      navigate(
        `/admin/business/${OrgId}/project/${projectId}/revision/${revisionID}/diagnosis`,
        navOpts
      );
    } else {
      navigate(
        `/project/${projectId}/revision/${revisionID}/diagnosis`,
        navOpts
      );
    }
  };

  const handleRowPrimaryClick = (e: any) => {
    if (status === RevisionStatus.SUCCESS) {
      ViewResult(e, id);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setHover(false)
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isBusyRevisionStatus = [
    RevisionStatus.PENDING,
    RevisionStatus.PROCESSING,
    RevisionStatus.VALIDATING,
  ].includes(status);
  const isTicketOngoing = Boolean(
    props.ticket && String(props.ticket.status || '').toLowerCase() === 'ongoing'
  );
  // Ticket ongoing used to hide the ⋮ menu for everyone. Keep it when the user
  // can still open inspection or delete the revision.
  const isActiveButtonMore = isBusyRevisionStatus
    ? false
    : isTicketOngoing
      ? canOpenInspectorPage || canShowDeleteRevision
      : true;

  return (
    <>
      <TableRow
        key={`row-${index}`}
        onMouseEnter={() => setHover(index)}
        onMouseLeave={() => setHover(null)}

      >
        <CustomTableCell clickable={false} status={status} collapseMode={notes} align='right'
          style={{ paddingLeft: "0px", border: "none", paddingRight: "0px" }} >
          {notes.length > 1 && (
            <IconButton
              key="expand-button"
              aria-label='expand row'
              size='small'
              onClick={() => {
                setOpenRow(!openRow);
              }}
            >
              {openRow ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </CustomTableCell>
        <CustomTableCell clickable={false} status={status} collapseMode onClick={handleRowPrimaryClick}>
          <span>{index}</span>
        </CustomTableCell>
        <CustomTableCell clickable={false} status={status} collapseMode sx={{ minWidth: '170px', maxWidth: "170px", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={handleRowPrimaryClick} dir='ltr'
        >
          <span>{formatFileName(files?.['idf']?.name)}</span>
        </CustomTableCell>
        <CustomTableCell clickable={false} status={status} collapseMode sx={{ minWidth: '100px', maxWidth: "100px", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={handleRowPrimaryClick}>
          <span>{createdBy}</span>
        </CustomTableCell>
        <CustomTableCell clickable={false} status={status} collapseMode sx={{ minWidth: "130px" }} onClick={handleRowPrimaryClick} >
          <span>{id}</span>
        </CustomTableCell>
        {showInspectionStatusColumn && <CustomTableCell clickable={false} status={status} collapseMode sx={{ textAlign: "flex-start" }} onClick={handleRowPrimaryClick}>
          {/* Feature window, or already assigned to an inspector — users must see inspection progress */}
          {(isInspectorAllowedStartDate || hasInspectorAssignment) && (
            <InspectionStatusBadge inspection={props.inspection} rtl={language === 'he'} />
          )}
        </CustomTableCell>}
        <CustomTableCell clickable={false} status={status} collapseMode sx={{ minWidth: "130px" }} onClick={handleRowPrimaryClick}>
          <span className='data'>
            {moment.utc(updatedAt).local().format('DD/MM/YY')}
          </span>
          <span className='time' style={{ marginLeft: '10px' }}>
            {moment.utc(updatedAt).local().format('HH:mm')}
          </span>
        </CustomTableCell>

        <CustomTableCell clickable={false} status={status} collapseMode onClick={handleRowPrimaryClick}>
          {report_1045_pass}
          <BadgeCustom pass={report_1045_pass} />
        </CustomTableCell>
        {projectType == "Residential" && <CustomTableCell clickable={false} status={status} collapseMode onClick={handleRowPrimaryClick}>
          <BadgeCustom pass={h_report_pass} />
        </CustomTableCell>}
        <CustomTableCell dir="ltr" clickable={false} status={status} collapseMode onClick={handleRowPrimaryClick}>
          {weighed_grade && parseFloat(weighed_grade).toFixed(2)}
        </CustomTableCell>


        <CustomTableCell clickable={false} status={status} collapseMode onClick={handleRowPrimaryClick}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <GradeBadge rating={rating} />
          </Box>
        </CustomTableCell>
        {/* 3D Viewer Column */}
        {hasProjectThreeDViewerAllowed  && 
        
        <CustomTableCell 
        clickable={false} 
        allowOverflow
        collapseMode status={status}  sx={{px:0, textAlign: 'center'}}>
          {status === RevisionStatus.SUCCESS && has3DRevisionStartDateAllowed && (
            <NewFeatureBadge>
              <ButtonIconSvg
                onClick={() => {
                  const code = btoa(id);
                  const viewerUrl = `/3d-viewer?code=${code}&revisionId=${id}&projectId=${projectId}`;
                  window.open(viewerUrl, '_blank');
                }}
              >
                <ThreeDIcon />
              </ButtonIconSvg>
            </NewFeatureBadge>
          )}
        </CustomTableCell>}
        <CustomTableCell clickable={false} collapseMode status={status} sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center', pt: '9px' }}>
          {ButtonsBasedOnStatus()}
        </CustomTableCell>


        {/* Menu Column */}
        <CustomTableCell clickable={false} status={status} collapseMode style={{ paddingLeft: "0px", border: "none", paddingRight: "0px" }} >
          {!isViewer ? isActiveButtonMore && (
            <Box >
              <ButtonIcon
                isActive={open}
                onClick={handleClick}
              >
                <MoreHorizIcon />
              </ButtonIcon>
              <Menu
                sx={{ fontSize: '14px' }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
              >
                <div>{menuBasedOnStatus()}</div>
              </Menu>
            </Box>
          ) :
            isViewerWithRevisionSuccessAndTransactionId && (
              <Box >
                <ButtonIcon
                  isActive={open}
                  onClick={handleClick}
                >
                  <MoreHorizIcon />
                </ButtonIcon>
                <Menu
                  sx={{ fontSize: '14px' }}
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                >
                  <div>{menuBasedOnStatus()}</div>
                </Menu>
              </Box>
            )
          }
        </CustomTableCell>
      </TableRow>
      <TableRow key={`collapse-row-${index}`} sx={{ boxShadow: 'none !important' }}>
        <TableCell sx={{
          paddingBottom: 0,
          paddingTop: 0,
          px: "59px",
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          borderRadius: '4px',
          backgroundColor: '#fff'
        }} colSpan={14} >
          <Collapse in={openRow} timeout="auto" unmountOnExit sx={{ width: '100%' }} >
            <RevisionNotes notes={notes} />
          </Collapse>
        </TableCell>
      </TableRow>
      {showLabsParseDialog && (
        <IdfParseResultStep
          open={showLabsParseDialog}
          parseErrors={labsParseErrors}
          nameFixes={labsNameFixes}
          zoneCount={labsZoneCount}
          onCancel={() => {
            setShowLabsParseDialog(false);
          }}
          onContinue={() => continueLabsParseFromBoard(false)}
          onFixAndConvert={() => continueLabsParseFromBoard(true)}
        />
      )}
      {showRevisionError && (
        <RevisionError
          key="revision-error"
          projectID={projectId}
          revisionID={id}
          businessID={OrgId}
          open={showRevisionError}
          setShowRevisionError={setShowRevisionError}
        />
      )}
      {/* Share Link Dialog */}
      <GenericDialog
        open={showShareLinkDialog}
        onClose={() => {
          setShowShareLinkDialog(false);
          setShareLinkData(null);
          setIsCopied(false);
        }}
        title={t('REVISION_MENU_SHAREABLE_LINK_TITLE') || 'Pixo Shareable Link'}
        paperSx={{width:"600px"}}
        cancelButtonText={t('CANCEL_BUTTON') || 'Close'}
        confirmButtonText={t('REVISION_MENU_COPY_PIXO_REPORT_LINK') || 'Copy Link'}
        onConfirm={handleCopyShareLink}
      >
        <Box sx={{ mt: 2, textAlign: 'left', width: '100%' }}>
          <TextField
            fullWidth
            label={t('REVISION_MENU_SHAREABLE_LINK_LABEL') || 'Shareable URL'}
            value={shareLinkData?.url || ''}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="copy to clipboard"
                    onClick={handleCopyShareLink}
                    edge="end"
                  >
                    {isCopied ? (
                      <CheckCircleIcon sx={{ color: '#1bb81e' }} />
                    ) : (
                      <ContentCopyIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Box>
      </GenericDialog>

      <GenericDialog
        open={openMembershipDialog}
        onClose={() => setOpenMembershipDialog(false)}
        title={t('SIMULATION_MEMBERSHIP_REQUIRED_TITLE')}
        onConfirm={() => {
          setOpenMembershipDialog(false);
          window.open('https://wa.me/972544780198', '_blank', 'noopener,noreferrer');
        }}
        confirmButtonText={t('SIMULATION_MEMBERSHIP_REQUIRED_CONTACT')}
        cancelButtonText={t('SIMULATION_MEMBERSHIP_REQUIRED_CANCEL')}
        maxWidth="md"
        paperSx={{ width: '100%', maxWidth: 550 }}
        contentSx={{ width: '100%', maxWidth: '100%' }}
        actionsAlign="space-around"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body1">{t('SIMULATION_MEMBERSHIP_REQUIRED_DESCRIPTION')}</Typography>
          <Typography variant="body1">{t('SIMULATION_MEMBERSHIP_REQUIRED_DESCRIPTION_2')}</Typography>
        </Box>
      </GenericDialog>
    </>
  );
};

export default RevisionRowData;